/**
 * 🎯 CONFIGURAÇÃO CENTRALIZADA DOS PLANOS HABITOP
 * 
 * ⚠️ ATENÇÃO: Este é o ÚNICO arquivo onde você deve alterar os limites dos planos!
 * Todas as alterações aqui se refletem automaticamente em todo o app.
 * 
 * 📝 COMO USAR:
 * 1. Altere os valores abaixo
 * 2. Salve o arquivo
 * 3. Todas as funcionalidades do app respeitarão automaticamente essas regras
 * 
 * 🔧 ESTRUTURA:
 * - BASIC_PLAN: Limites do plano gratuito
 * - PRO_PLAN: Recursos do plano pago
 * - FEATURE_FLAGS: Controle de funcionalidades disponíveis
 */

// ============================================================================
// 🆓 PLANO BÁSICO - LIMITES E RECURSOS
// ============================================================================
export const BASIC_PLAN = {
  // 📊 LIMITES DE CADASTRO
  limits: {
    maxHabits: 5,           // Máximo de hábitos permitidos
    maxGoals: 3,            // Máximo de metas permitidas
    maxChallenges: 1,       // Máximo de desafios permitidos
    maxNotes: 5,            // Máximo de notas permitidas (0 = não disponível)
    maxEvents: 5,    // Máximo de eventos (Infinity = ilimitado)
    
    // 🎯 PRODUTIVIDADE
    maxVideos: 2,           // Máximo de vídeos para foco
    maxPlaylists: 1,        // Máximo de playlists do Spotify
    maxPomodoroCustomization: false, // Personalização do Pomodoro
  },

  // ✅ FUNCIONALIDADES DISPONÍVEIS
  features: {
    canAccessHabits: true,           // Acesso à aba Hábitos
    canAccessCalendar: true,         // Acesso à aba Calendário
    canAccessMenu: true,             // Acesso ao menu principal
    canAccessSettings: true,         // Acesso às configurações
    canAccessHelp: true,             // Acesso à ajuda
    canAccessStatistics: true,       // Acesso às estatísticas (limitado)
    canAccessGoals: true,            // Acesso às metas
    canAccessChallenges: true,       // Acesso aos desafios
    canAccessNotes: true,            // Acesso às notas
    canAccessProductivity: false,    // Acesso à produtividade
    canAccessMeditations: false,     // Acesso às meditações
    canAccessBooks: false,           // Acesso às recomendações de livros
    canAccessEvents: true,           // Acesso aos eventos
  },

  // 📈 ESTATÍSTICAS DISPONÍVEIS
  statistics: {
    canSeeAllStatistics: false,      // Ver todos os gráficos
    canSeeOnlyBasicStatistics: true, // Ver apenas estatísticas básicas
    availableGraphs: ['graph02', 'cards'], // Gráficos disponíveis
    restrictedGraphs: ['graph01', 'graph03', 'graph04', 'advanced'], // Gráficos restritos
  },

  // 🎨 PERSONALIZAÇÃO
  customization: {
    canCustomizeColors: true,        // Personalizar cores
    canCustomizeSounds: true,        // Personalizar sons
    canCustomizeCalendarLayout: false, // Layout do calendário
    canCustomizePomodoro: false,     // Configurações do Pomodoro
  }
};

// ============================================================================
// ⭐ PLANO PRO - RECURSOS ILIMITADOS
// ============================================================================
export const PRO_PLAN = {
  // 📊 LIMITES DE CADASTRO (ILIMITADOS)
  limits: {
    maxHabits: Infinity,             // Hábitos ilimitados
    maxGoals: Infinity,              // Metas ilimitadas
    maxChallenges: Infinity,         // Desafios ilimitados
    maxNotes: Infinity,              // Notas ilimitadas
    maxEvents: Infinity,             // Eventos ilimitados
    
    // 🎯 PRODUTIVIDADE
    maxVideos: Infinity,             // Vídeos ilimitados
    maxPlaylists: Infinity,          // Playlists ilimitadas
    maxPomodoroCustomization: true,  // Personalização total do Pomodoro
  },

  // ✅ FUNCIONALIDADES DISPONÍVEIS (TODAS)
  features: {
    canAccessHabits: true,           // Acesso à aba Hábitos
    canAccessCalendar: true,         // Acesso à aba Calendário
    canAccessMenu: true,             // Acesso ao menu principal
    canAccessSettings: true,         // Acesso às configurações
    canAccessHelp: true,             // Acesso à ajuda
    canAccessStatistics: true,       // Acesso total às estatísticas
    canAccessGoals: true,            // Acesso às metas
    canAccessChallenges: true,       // Acesso aos desafios
    canAccessNotes: true,            // Acesso às notas
    canAccessProductivity: true,     // Acesso à produtividade
    canAccessMeditations: true,      // Acesso às meditações
    canAccessBooks: true,            // Acesso às recomendações de livros
    canAccessEvents: true,           // Acesso aos eventos
  },

  // 📈 ESTATÍSTICAS DISPONÍVEIS (TODAS)
  statistics: {
    canSeeAllStatistics: true,       // Ver todos os gráficos
    canSeeOnlyBasicStatistics: false, // Não limitado às básicas
    availableGraphs: ['graph01', 'graph02', 'graph03', 'graph04', 'advanced'], // Todos os gráficos
    restrictedGraphs: [],            // Nenhum gráfico restrito
  },

  // 🎨 PERSONALIZAÇÃO (TOTAL)
  customization: {
    canCustomizeColors: true,        // Personalizar cores
    canCustomizeSounds: true,        // Personalizar sons
    canCustomizeCalendarLayout: true, // Layout do calendário
    canCustomizePomodoro: true,      // Configurações do Pomodoro
  }
};

// ============================================================================
// 🔧 FUNÇÕES UTILITÁRIAS PARA VERIFICAÇÕES
// ============================================================================

/**
 * Verifica se o usuário pode acessar uma funcionalidade específica
 * @param {string} plan - Plano do usuário ('BASIC' ou 'PRO')
 * @param {string} feature - Funcionalidade a verificar
 * @returns {boolean} - Se pode acessar ou não
 */
export function canAccessFeature(plan, feature) {
  const planConfig = plan === 'PRO' ? PRO_PLAN : BASIC_PLAN;
  return planConfig.features[feature] || false;
}

/**
 * Verifica se o usuário atingiu o limite de um recurso
 * @param {string} plan - Plano do usuário ('BASIC' ou 'PRO')
 * @param {string} resource - Recurso a verificar
 * @param {number} currentCount - Quantidade atual
 * @returns {boolean} - Se atingiu o limite ou não
 */
export function hasReachedLimit(plan, resource, currentCount) {
  if (plan === 'PRO') return false; // Plano PRO não tem limites
  
  const planConfig = BASIC_PLAN;
  const limit = planConfig.limits[resource];
  
  // Debug: log para verificar a validação de limite
  console.log('hasReachedLimit Debug:', { plan, resource, currentCount, limit, reached: currentCount >= limit });
  
  if (limit === Infinity) return false; // Recurso ilimitado
  if (limit === 0) return true; // Recurso não disponível
  
  return currentCount >= limit;
}

/**
 * Obtém o limite máximo de um recurso para um plano
 * @param {string} plan - Plano do usuário ('BASIC' ou 'PRO')
 * @param {string} resource - Recurso a verificar
 * @returns {number|Infinity} - Limite máximo
 */
export function getResourceLimit(plan, resource) {
  const planConfig = plan === 'PRO' ? PRO_PLAN : BASIC_PLAN;
  const limit = planConfig.limits[resource] || 0;
  
  // Debug: log para verificar o limite
  console.log('getResourceLimit Debug:', { plan, resource, limit, planConfig: planConfig.limits });
  
  return limit;
}

/**
 * Verifica se uma funcionalidade de estatísticas está disponível
 * @param {string} plan - Plano do usuário ('BASIC' ou 'PRO')
 * @param {string} graphType - Tipo de gráfico
 * @returns {boolean} - Se está disponível
 */
export function canSeeStatistics(plan, graphType) {
  const planConfig = plan === 'PRO' ? PRO_PLAN : BASIC_PLAN;
  
  if (planConfig.statistics.canSeeAllStatistics) return true;
  
  return planConfig.statistics.availableGraphs.includes(graphType);
}

/**
 * Obtém todas as permissões de um plano
 * @param {string} plan - Plano do usuário ('BASIC' ou 'PRO')
 * @returns {object} - Todas as permissões do plano
 */
export function getPlanPermissions(plan) {
  const planConfig = plan === 'PRO' ? PRO_PLAN : BASIC_PLAN;
  
  return {
    ...planConfig.features,
    ...planConfig.statistics,
    ...planConfig.customization,
    limits: planConfig.limits
  };
}

// ============================================================================
// 📋 RESUMO RÁPIDO DOS PLANOS (para referência)
// ============================================================================
export const PLAN_SUMMARY = {
  BASIC: {
    name: 'BÁSICO',
    price: 'R$ 19,90',
    period: 'por mês',
    highlights: [
      'Até 5 hábitos',
      'Até 3 metas',
      'Até 1 desafio',
      'Ate 5 eventos',
      'Estatísticas básicas',
      'Recomendações de livros',
      'Personalização de cores e sons',
      'Personalização de layout do calendário',
    ]
  },
  PRO: {
    name: 'PRO',
    price: 'R$ 29,90',
    period: 'por mês',
    highlights: [
      'Hábitos ILIMITADOS',
      'Metas ILIMITADAS',
      'Desafios ILIMITADOS',
      'Notas ILIMITADAS',
      'Estatísticas COMPLETAS',
      'Recomendações de livros',
      'Personalização de cores e sons',
      'Personalização de layout do calendário',
    ]
  }
};

// ============================================================================
// 🚀 EXPORTAÇÕES PRINCIPAIS
// ============================================================================
export default {
  BASIC_PLAN,
  PRO_PLAN,
  canAccessFeature,
  hasReachedLimit,
  getResourceLimit,
  canSeeStatistics,
  getPlanPermissions,
  PLAN_SUMMARY
};
